import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  useRemoveTenantChat360Fallback, useSaveTenantChat360Fallback, useTenantChat360Fallback, useTestTenantChat360Fallback,
} from "../../lib/api";
import { FieldError, FieldLabel, GhostButton, PrimaryButton, SecondaryButton, ToggleRow, inputClass } from "./primitives";

// This brand's fallback Chat360 account: a second WhatsApp number that a booking's
// group is made on when Chat360 refuses the main number. Like the main account, the
// password and API key are stored encrypted and never come back to the browser.
const BLANK = { enabled: true, client_number: "", login_email: "", base_url: "", login_password: "", task_api_key: "" };

export default function Chat360FallbackEditor({ tenant }) {
  const { data, isLoading } = useTenantChat360Fallback(tenant.id);
  const save = useSaveTenantChat360Fallback(tenant.id);
  const remove = useRemoveTenantChat360Fallback(tenant.id);
  const test = useTestTenantChat360Fallback(tenant.id);
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  const baseline = useMemo(
    () => ({
      ...BLANK,
      enabled: data?.saved ? Boolean(data.enabled) : true,
      client_number: data?.client_number || "",
      login_email: data?.login_email || "",
      base_url: data?.base_url || "",
    }),
    [data],
  );

  useEffect(() => {
    setForm(baseline);
  }, [baseline]);

  // What is on screen belongs to one brand: nothing carries over to the next.
  useEffect(() => {
    setResult(null);
    setAdding(false);
    setErrors({});
  }, [tenant.id]);

  if (isLoading) return null;

  const heading = <h2 className="text-[16px] font-semibold text-[#111827]">Fallback account</h2>;

  if (data?.migration_pending) {
    return (
      <div title={`GET /api/superadmin/tenants/${tenant.id}/chat360/fallback`} className="mt-8 max-w-[560px] border-t border-[#F0F1F3] pt-6">
        {heading}
        <p className="mt-3 text-[13px] text-[#B45309]">
          Not set up on this server yet. Ask the developer to run the latest database update, then reload.
        </p>
      </div>
    );
  }

  if (!data?.saved && !adding) {
    return (
      <div title={`GET /api/superadmin/tenants/${tenant.id}/chat360/fallback`} className="mt-8 max-w-[560px] border-t border-[#F0F1F3] pt-6">
        {heading}
        <p className="mt-3 text-[13px] text-[#4B5563]">No fallback account. If Chat360 refuses the main number, the booking&apos;s group fails.</p>
        <div className="mt-3">
          <SecondaryButton onClick={() => setAdding(true)} data-testid="fallback-add">Add fallback account</SecondaryButton>
        </div>
      </div>
    );
  }

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors({});
    setResult(null);
  };
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const persist = async () => {
    const number = form.client_number.replace(/\D/g, "");
    if (!number) {
      setErrors({ client_number: "Enter the fallback number: digits only, including the country code." });
      return false;
    }
    try {
      await save.mutateAsync({
        enabled: form.enabled,
        client_number: number,
        base_url: form.base_url.trim(),
        login_email: form.login_email.trim(),
        // Left out unless being replaced, so a blank field never wipes a stored secret.
        ...(form.login_password ? { login_password: form.login_password } : {}),
        ...(form.task_api_key ? { task_api_key: form.task_api_key } : {}),
      });
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Save failed.";
      if (/number/i.test(detail)) setErrors({ client_number: detail });
      else toast.error(detail);
      return false;
    }
  };

  const onSave = async () => {
    if (await persist()) toast.success("Fallback account saved.");
  };

  // The test runs against what is stored, so unsaved edits are saved first.
  const onTest = async () => {
    setResult(null);
    if ((dirty || !data?.saved) && !(await persist())) return;
    try {
      setResult(await test.mutateAsync());
    } catch (e) {
      setResult({ ok: false, detail: e?.response?.data?.detail || "Could not reach Chat360." });
    }
  };

  const onRemove = async () => {
    if (!data?.saved) {
      setAdding(false);
      setForm(baseline);
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove the fallback account? Groups already made on its number will stop receiving messages from us.")) return;
    try {
      await remove.mutateAsync();
      setAdding(false);
      toast.success("Fallback account removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove it.");
    }
  };

  const secretPlaceholder = (saved) => (saved ? "Saved · enter a new one to replace" : "Blank = same as main account");

  return (
    <div title={`GET /api/superadmin/tenants/${tenant.id}/chat360/fallback`} className="mt-8 max-w-[560px] border-t border-[#F0F1F3] pt-6">
      {heading}

      <div className="mt-4 flex flex-col gap-5">
        <ToggleRow
          title="Use the fallback number"
          checked={form.enabled}
          onChange={(enabled) => set({ enabled })}
        />

        <div>
          <FieldLabel>Fallback WhatsApp number</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.client_number}
            placeholder="918799952623"
            onChange={(e) => set({ client_number: e.target.value })}
            data-testid="fallback-number"
          />
          <FieldError>{errors.client_number}</FieldError>
        </div>

        <div>
          <FieldLabel>Login email</FieldLabel>
          <input
            type="email"
            className={inputClass}
            value={form.login_email}
            placeholder="Blank = same as main account"
            onChange={(e) => set({ login_email: e.target.value })}
            data-testid="fallback-email"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>Password</FieldLabel>
            <input
              type="password"
              className={inputClass}
              value={form.login_password}
              placeholder={secretPlaceholder(data?.has_password)}
              onChange={(e) => set({ login_password: e.target.value })}
              data-testid="fallback-password"
            />
          </div>
          <div>
            <FieldLabel>Task API key</FieldLabel>
            <input
              type="password"
              className={inputClass}
              value={form.task_api_key}
              placeholder={secretPlaceholder(data?.has_task_api_key)}
              onChange={(e) => set({ task_api_key: e.target.value })}
              data-testid="fallback-api-key"
            />
          </div>
        </div>

        <div>
          <FieldLabel>Base URL</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.base_url}
            placeholder="Blank = same as main account"
            onChange={(e) => set({ base_url: e.target.value })}
          />
        </div>

        <div className="rounded-xl bg-[#FFFBEB] px-4 py-3 text-[13px] leading-5 text-[#92400E]">
          <strong className="font-semibold">Before relying on it,</strong> check two things on this number in Chat360: the same message
          templates are approved under the same names, and its incoming messages are sent to this product. Without them a group
          is created but its messages fail or its replies never arrive.
        </div>

        {result && (
          <div
            className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-[13px] text-[#4B5563] shadow-[0_0_0_1px_#E5E7EB]"
            data-testid="fallback-test-result"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${result.ok ? "bg-[#16A34A]" : "bg-[#DC2626]"}`} />
            {result.detail}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save fallback
          </PrimaryButton>
          <SecondaryButton onClick={onTest} disabled={test.isPending}>
            {test.isPending && <Loader2 className="animate-spin" size={14} />}
            Test connection
          </SecondaryButton>
          <GhostButton onClick={onRemove} disabled={remove.isPending}>{data?.saved ? "Remove" : "Cancel"}</GhostButton>
        </div>
      </div>
    </div>
  );
}
